import { Menu, MenuItem, Stack, Theme } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { isSameDay } from "date-fns";
import DOMPurify from "dompurify";
import { ChevronRight, GraduationCap, LucideIcon, MapPin, Settings, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CardContent,
  CardRoot,
  ChevronWrapper, LiveEventActionsMenu, LiveEventInfoRow,
  StatusLabel,
  StatusRow,
  SubtitleText,
  TimeText,
  TitleText
} from "./AgendaEventCard.style";
import { StatusBox } from "../../components/StatusBox/StatusBox";
import { useType } from "../../hooks";
import { useNavigate } from "../../services";
import type { ContentType, JavaBasicDate } from "../../types";
import type { DayCourse } from "../CalendarCarouselCard/CalendarCarouselCard.types";

type EventCardType = "eventoLive" | "scadenza" | "obbligatorio" | "smartLearning" | "default";

type AgendaEventCardProps = {
  course: Exclude<DayCourse, { dayCourseType: "empty" }>
  onClick?: () => void
  selectAction?: (action: string, date: Date, slotId: number, start: string, end: string) => void
  setShowSmartLearning?: () => void
};

export function AgendaEventCard({ course, onClick, selectAction, setShowSmartLearning }: AgendaEventCardProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const eventType = getEventType(course);
  const borderColor = getEventColor(eventType, theme);
  const { label: eventText } = getEventInfo(eventType, t);
  const timeStr = getTimeString(course);

  const rawTypeLabel = useType(
    course.learningObjectType,
    course.dayCourseType === "learningObject"
      ? (course.learningObjectTypology ?? undefined)
      : undefined
  );
  const typeLabel = course.dayCourseType === "learningObject" ? rawTypeLabel : "";
  const title = course.dayCourseType === "smartLearning"
    ? t("learning_time")
    : course.title ?? "";

  // const participated = eventType === "eventoLive"
  //   && course.dayCourseType === "learningObject"
  //   && course.status === "C";

  // const liveIcon: LucideIcon | null = eventType === "eventoLive"
  //   ? (participated ? SquareCheck : TriangleAlert)
  //   : null;

  // const liveIconBgColor = eventType === "eventoLive"
  //   ? (participated ? theme.customColors.backgroundSecondaryComplete : theme.customColors.backgroundWarning)
  //   : null;

  // const ActionIcon = liveIcon ?? StatusIcon;
  // const actionBgColor = liveIconBgColor ?? theme.customColors.backgroundWarning;

  const teacherNames = eventType === "eventoLive" && course.dayCourseType === "learningObject"
    ? course.sessions
      ? course.sessions.flatMap((s) => s.teachers).map((t) => t.completeName).join("<br/>")
      : (course.stakeholders ?? []).filter((s) => s.visStakeholder).map((s) => s.name).join("<br/>")
    : "";

  const locationText = eventType === "eventoLive" && course.dayCourseType === "learningObject"
    ? course.sessions?.[0]?.location ?? ""
    : "";

  function handleClick() {
    if (onClick) {
      onClick();
      return;
    }
    if (course.dayCourseType === "learningObject" && course.learningObjectTypology) {
      navigate.navigateToDetail({
        id: course.id,
        learningObjectTypology: course.learningObjectTypology,
        courseId: course.parentId ?? undefined,
        rootId: course.grandParentId ?? undefined
      });
    }
  }

  const handleStatusIcon = () => (
    <StatusBox
      status={ course.status  } 
      isMandatory={ course.isMandatory }
    />
  );

  return (
    <CardRoot
      tabIndex={ 0 }
      role="button"
      aria-label={ `${eventText} - ${title}` }
      onClick={ ()=> {
        if(course.dayCourseType === "smartLearning" && setShowSmartLearning){
          return setShowSmartLearning();
        }
        handleClick(); 
      }
      } 
      onKeyDown={ (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          if(course.dayCourseType === "smartLearning" && setShowSmartLearning){
            return setShowSmartLearning();
          }
          handleClick();
        }
      } }
    >
      <CardContent
        borderColor={ borderColor }
      >
        <StatusRow>
          <Stack direction={ "row" } alignItems={ "center" } gap={ "4px" }>
            <StatusLabel statuscolor={ borderColor }>
              { eventText }
            </StatusLabel>
            { course.dayCourseType === "smartLearning" && selectAction && (
              <>
                <LiveEventActionsMenu
                  component="button"
                  onClick={ (e) => {
                    e.stopPropagation();
                    setMenuAnchor(e.currentTarget as HTMLElement);
                  } }
                  aria-label={ t("modify_delete") }
                >
                  <Settings aria-hidden="true" size={ 16 } color={ theme.customColors.textPrimary } />
                </LiveEventActionsMenu>
                <Menu
                  anchorEl={ menuAnchor }
                  open={ Boolean(menuAnchor) }
                  onClose={ () => setMenuAnchor(null) }
                >
                  <MenuItem
                    onClick={ () => {
                      selectAction?.("modify", new Date(course.date?.join("/") ?? ""), course.id, formatTime(course.startTime.replaceAll(" ", "")), formatTime(course.endTime.replaceAll(" ", "")));
                      setMenuAnchor(null);
                    } }
                  >
                    { t("modify") }
                  </MenuItem>
                  <MenuItem
                    onClick={ () => {
                      selectAction?.("remove", new Date(course.date?.join("/") ?? ""), course.id, formatTime(course.startTime.replaceAll(" ", "")), formatTime(course.endTime.replaceAll(" ", "")));
                      setMenuAnchor(null);
                    } }
                  >
                    { t("delete") }
                  </MenuItem>
                </Menu>
              </>
            ) }
          </Stack>
        </StatusRow>
        <Stack 
          direction={ "row" } 
          alignItems={ "center" } 
          justifyContent={ "space-between" } 
          gap={ "0.25rem" }
        >
          <TitleText>{ title }</TitleText>
        </Stack>
        { typeLabel && <SubtitleText>{ typeLabel }</SubtitleText> }
        <LiveEventInfoRow>
          { locationText && (
            <Stack direction={ "row" } alignItems={ "center" } gap={ "8px" }>
              <MapPin aria-hidden="true" size={ 16 } color={ theme.customColors.exploreCardText }/>
              <SubtitleText>{ locationText }</SubtitleText>
            </Stack>
          ) }
          { teacherNames && (
            <Stack direction={ "row" } alignItems={ "center" } gap={ "8px" }>
              <GraduationCap aria-hidden="true" size={ 16 } color={ theme.customColors.exploreCardText }/>
              <SubtitleText dangerouslySetInnerHTML={ { __html: DOMPurify.sanitize(teacherNames) } } />
            </Stack>
          ) }
        </LiveEventInfoRow>
      </CardContent>
      <Stack
        gap={ "0.25rem" }
        alignItems={ "end" }
      >
        { handleStatusIcon() }
        { timeStr && (
          <TimeText>
            { timeStr }
          </TimeText>
        ) }
      </Stack>
      { course.dayCourseType !== "smartLearning" && (
        <ChevronWrapper
          onClick={ (e) => {
            e.stopPropagation();
            handleClick();
          } }
          onKeyDown={ (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              handleClick();
            }
          } }
          tabIndex={ 0 }
          role="button"
          aria-label={ t("go_to_detail") }
        >
          <ChevronRight aria-hidden="true" size={ 20 } color={ theme.customColors.textPrimary } />
        </ChevronWrapper>
      ) }
    </CardRoot>
  );
}

function getEventType(
  course: Exclude<DayCourse, { dayCourseType: "empty" }>
): EventCardType {
  if (course.dayCourseType === "smartLearning") {
    return "smartLearning";
  }

  if (course.dayCourseType === "learningObject") {
    const isLive =
      course.learningObjectType === "SYNC"
      || course.learningObjectType === "LIVE" as ContentType
      || course.learningObjectType === "VIRTUAL_CLASSROOM" as ContentType
      || course.learningObjectType === "LIVE_CLASSROOM" as ContentType;

    if (course.isMandatory) {
      if (isExpiring(course.date, course.expirationDate ?? undefined)) {
        return "scadenza";
      }
      if (isLive) {
        return "eventoLive";
      }
      return "obbligatorio";
    }

    if (isLive) {
      return "eventoLive";
    }
  }

  return "default";
}

function getEventColor(eventType: EventCardType, theme: Theme): string {
  switch (eventType) {
  case "eventoLive":
    return theme.customColors.borderSecondaryComplete;
  case "scadenza":
    return theme.customColors.textWarning;
  case "smartLearning":
    return theme.customColors.systemSecondary01;
  default:
    return theme.customColors.systemSecondary01;
  }
}

function getEventInfo(
  eventType: EventCardType,
  t: (key: string) => string
): { icon: LucideIcon | null; label: string } {
  switch (eventType) {
  case "eventoLive":
    return { icon: null, label: t("evento_live") };
  case "scadenza":
    return { icon: TriangleAlert, label: t("expiring") };
  case "obbligatorio":
    return { icon: null, label: t("mandatory") };
  case "smartLearning":
    return { icon: null, label: t("smart_learning") };
  default:
    return { icon: null, label: t("digital") };
  }
}

function getTimeString(
  course: Exclude<DayCourse, { dayCourseType: "empty" }>
): string {

  if (course.dayCourseType === "smartLearning") {
    const startTime = (course.startTime.replaceAll(" ", "") ?? "");
    const endTime = (course.endTime.replaceAll(" ", "") ?? "");
    return formatTime(`${startTime} - ${endTime}`);
  }

  if (course.dayCourseType === "learningObject") {
    if (course.startDateTime && course.endDateTime) {
      const start = new Date(course.startDateTime);
      const end = new Date(course.endDateTime);
      return (
        `${padTime(start.getHours())}:${padTime(start.getMinutes())}` +
        " - " +
        `${padTime(end.getHours())}:${padTime(end.getMinutes())}`
      );
    }
    if (course.startTime && course.endTime) {
      const startTime = (course.startTime.replaceAll(" ", "") ?? "");
      const endTime = (course.endTime.replaceAll(" ", "") ?? "");
      return formatTime(`${startTime} - ${endTime}`);
    }
  }

  return "";
}

function formatTime(time: string): string {
  if (!time) return "";
  const parts = time.trim().split(":");
  if (parts.length < 2) return time;
  return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
}

function padTime(n: number): string {
  return String(n).padStart(2, "0");
}

function isExpiring(
  date: JavaBasicDate | null,
  expirationDate?: JavaBasicDate
): boolean {
  if (!date || !expirationDate) return false;
  return isSameDay(new Date(date.join("/")), new Date(expirationDate.join("/")));
}
